/**
 * Générateur de prompts pour les agents vocaux IA guidés
 * Templates prédéfinis pour différents cas d'usage
 */

export interface TemplateConfig {
  agent_name: string;
  company_name: string;
  tone: 'pro' | 'warm' | 'brief';
  minutes_advance: number;
  offer_reschedule: boolean;
  max_retries: number;
  closing_phrase?: string;
  language?: string; // 'fr' ou 'en'
}

export interface GeneratedPrompt {
  first_message: string;
  system_prompt: string;
}

/**
 * Template: Confirmation de rendez-vous (appel sortant)
 */
export function generateAppointmentReminderPrompt(config: TemplateConfig): GeneratedPrompt {
  const language = config.language || 'fr';
  
  if (language === 'en') {
    return generateAppointmentReminderPromptEN(config);
  }
  
  return generateAppointmentReminderPromptFR(config);
}

function generateAppointmentReminderPromptFR(config: TemplateConfig): GeneratedPrompt {
  const toneDescriptions = {
    pro: 'professionnel et courtois',
    warm: 'chaleureux et bienveillant',
    brief: 'direct et concis'
  };

  const closingPhrase = config.closing_phrase || "Merci et bonne journée";
  const agentName = config.agent_name || "l'assistant virtuel";

  const first_message = `Bonjour, moi c'est ${agentName} de ${config.company_name}, est-ce que je parle à {{client_name}} ?`;

  const system_prompt = `Tu es ${agentName}, l'assistant virtuel de ${config.company_name}. Ton rôle est de confirmer les rendez-vous par téléphone. Tu es ${toneDescriptions[config.tone]}.

CONTEXTE DE L'APPEL :
- Client : {{client_name}}
- Rendez-vous : {{appointment_date}} à {{appointment_time}}
- Notes : {{appointment_notes}}
- Numéro de téléphone : {{client_phone}}

INSTRUCTIONS :
1. Vérifie l'identité : "Je cherche à joindre {{client_name}}, est-ce bien vous ?"
   - Si NON → excuse-toi poliment et termine l'appel
   - Si OUI → continue

2. Pose LA question principale : "Je vous appelle pour confirmer votre rendez-vous du {{appointment_date}} à {{appointment_time}}. Serez-vous présent à ce rendez-vous ?"

3. Attends la réponse du client

4. Selon la réponse :
   - Si OUI → Confirme et rappelle de venir ${config.minutes_advance} minutes en avance
   - Si NON → ${config.offer_reschedule ? 'Propose de reporter le rendez-vous' : 'Remercie et termine poliment'}

RÈGLES STRICTES :
- Utilise EXACTEMENT les informations fournies (nom, date, heure)
- Ne mentionne JAMAIS une date/heure différente
- Reste sur le sujet du rendez-vous
- Sois bref (max 2 minutes)
- Si le client veut annuler ou modifier son rendez-vous, tu as déjà son numéro de téléphone en contexte et tu l'utilises au besoin

## ⛔ RÈGLE CRITIQUE — VALIDATION OBLIGATOIRE
- Tu NE DOIS JAMAIS confirmer ou conclure tant que tu n'as pas entendu clairement "OUI" ou "NON"
- Le silence, les bruits, "hmm", "ok", respirations, ou phrases ambiguës NE SONT PAS des réponses
- Si la réponse n'est pas clairement OUI ou NON :
  → repose la question
  → avec une phrase PLUS COURTE
- Après ${config.max_retries} tentatives sans réponse claire :
  → dis que tu n'as pas pu obtenir confirmation
  → termine l'appel poliment

## 🔁 LOGIQUE DE DÉCISION
Après chaque réponse du client :
- Si la réponse contient clairement OUI → branche OUI
- Si la réponse contient clairement NON → branche NON
- Sinon → considère la réponse comme INCERTAINE
  - reformule la question
  - maximum ${config.max_retries} fois

FIN D'APPEL :
Termine toujours par : "${closingPhrase}"`;

  return {
    first_message,
    system_prompt
  };
}

function generateAppointmentReminderPromptEN(config: TemplateConfig): GeneratedPrompt {
  const toneDescriptions = {
    pro: 'professional and courteous',
    warm: 'warm and caring',
    brief: 'direct and concise'
  };

  const closingPhrase = config.closing_phrase || "Thank you and have a great day";
  const agentName = config.agent_name || "the virtual assistant";

  const first_message = `Hello, this is ${agentName} from ${config.company_name}, am I speaking with {{client_name}}?`;

  const system_prompt = `You are ${agentName}, the virtual assistant for ${config.company_name}. Your role is to confirm appointments by phone. You are ${toneDescriptions[config.tone]}.

CALL CONTEXT:
- Client: {{client_name}}
- Appointment: {{appointment_date}} at {{appointment_time}}
- Notes: {{appointment_notes}}
- Phone number: {{client_phone}}

INSTRUCTIONS:
1. Verify identity: "I'm looking for {{client_name}}, is this you?"
   - If NO → apologize politely and end the call
   - If YES → continue

2. Ask THE main question: "I'm calling to confirm your appointment on {{appointment_date}} at {{appointment_time}}. Will you be present for this appointment?"

3. Wait for the client's response

4. Based on the response:
   - If YES → Confirm and remind to arrive ${config.minutes_advance} minutes early
   - If NO → ${config.offer_reschedule ? 'Offer to reschedule the appointment' : 'Thank them and end politely'}

STRICT RULES:
- Use EXACTLY the information provided (name, date, time)
- NEVER mention a different date/time
- Stay on the topic of the appointment
- Be brief (max 2 minutes)
- If the client wants to cancel or modify their appointment, you already have their phone number in context and use it as needed

## ⛔ CRITICAL RULE — MANDATORY VALIDATION
- You MUST NEVER confirm or conclude until you have clearly heard "YES" or "NO"
- Silence, noises, "hmm", "ok", breathing, or ambiguous phrases ARE NOT responses
- If the response is not clearly YES or NO:
  → ask the question again
  → with a SHORTER phrase
- After ${config.max_retries} attempts without a clear response:
  → say you couldn't obtain confirmation
  → end the call politely

## 🔁 DECISION LOGIC
After each client response:
- If the response clearly contains YES → YES branch
- If the response clearly contains NO → NO branch
- Otherwise → consider the response as UNCERTAIN
  - rephrase the question
  - maximum ${config.max_retries} times

END OF CALL:
Always end with: "${closingPhrase}"`;

  return {
    first_message,
    system_prompt
  };
}

/**
 * Domaines disponibles pour organiser les templates
 */
export const TEMPLATE_DOMAINS = {
  healthcare: {
    id: 'healthcare',
    name: 'Santé & Bien-être',
    color: 'blue'
  },
  retail: {
    id: 'retail',
    name: 'Commerce & Retail',
    color: 'green'
  },
  services: {
    id: 'services',
    name: 'Services professionnels',
    color: 'purple'
  },
  education: {
    id: 'education',
    name: 'Éducation',
    color: 'orange'
  },
  real_estate: {
    id: 'real_estate',
    name: 'Immobilier',
    color: 'indigo'
  }
} as const;

export type TemplateDomain = keyof typeof TEMPLATE_DOMAINS;

/**
 * Interface pour un template d'agent
 */
export interface AgentTemplate {
  id: string;
  name: string;
  description: string;
  domain: TemplateDomain;
  requiredVariables: string[];
  recommendedActions: string[];
  generator: (config: TemplateConfig) => GeneratedPrompt;
  icon?: string; // Optionnel : icône spécifique au template
}

/**
 * Liste des templates disponibles, organisés par domaine
 */
export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: 'appointment_reminder',
    name: 'Confirmation de rendez-vous',
    description: 'Appels de rappel automatisés, script guidé, variables RDV requises',
    domain: 'healthcare',
    requiredVariables: ['agent_name', 'client_name', 'appointment_date', 'appointment_time', 'appointment_notes', 'client_phone'],
    recommendedActions: ['calendar', 'cancel_appointment', 'reschedule_appointment'],
    generator: generateAppointmentReminderPrompt
  }
  // Exemples futurs (commentés pour référence) :
  // {
  //   id: 'appointment_reminder_retail',
  //   name: 'Rappel de rendez-vous client',
  //   description: 'Rappels pour consultations, essais, visites showroom',
  //   domain: 'retail',
  //   requiredVariables: ['agent_name', 'client_name', 'appointment_date', 'appointment_time', 'client_phone'],
  //   recommendedActions: ['calendar', 'reschedule_appointment'],
  //   generator: generateAppointmentReminderPrompt
  // },
  // {
  //   id: 'order_confirmation',
  //   name: 'Confirmation de commande',
  //   description: 'Confirmation de commande et suivi de livraison',
  //   domain: 'retail',
  //   requiredVariables: ['agent_name', 'client_name', 'order_number', 'order_date', 'client_phone'],
  //   recommendedActions: ['sms'],
  //   generator: generateOrderConfirmationPrompt
  // }
];

/**
 * Grouper les templates par domaine
 */
export function getTemplatesByDomain(): Record<TemplateDomain, AgentTemplate[]> {
  const grouped: Record<string, AgentTemplate[]> = {};
  
  // Initialiser tous les domaines avec un tableau vide
  Object.keys(TEMPLATE_DOMAINS).forEach(domain => {
    grouped[domain] = [];
  });
  
  // Grouper les templates
  AGENT_TEMPLATES.forEach(template => {
    if (!grouped[template.domain]) {
      grouped[template.domain] = [];
    }
    grouped[template.domain].push(template);
  });
  
  return grouped as Record<TemplateDomain, AgentTemplate[]>;
}

/**
 * Valeurs par défaut pour la configuration du template
 */
export const DEFAULT_TEMPLATE_CONFIG: TemplateConfig = {
  agent_name: '',
  company_name: '',
  tone: 'pro',
  minutes_advance: 10,
  offer_reschedule: true,
  max_retries: 2,
  closing_phrase: 'Merci et bonne journée',
  language: 'fr'
};

